import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { toast } from 'react-hot-toast';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 100000, // Reduced timeout to 10 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add request timestamp for debugging
    config.metadata = { startTime: new Date() };
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and performance monitoring
apiClient.interceptors.response.use(
  (response) => {
    // Log response time for performance monitoring
    const endTime = new Date();
    const duration = endTime - response.config.metadata.startTime;
    
    if (duration > 2000) {
      console.warn(`Slow API response: ${response.config.url} took ${duration}ms`);
    }
    
    return response;
  },
  (error) => {
    // Log error response time
    if (error.config?.metadata) {
      const endTime = new Date();
      const duration = endTime - error.config.metadata.startTime;
      console.error(`API Error after ${duration}ms:`, {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        message: error.response?.data?.message || error.message,
      });
    }
    
    // Handle common errors
    if (error.response?.status === 401) {
      // Clear auth data and redirect to login
      localStorage.removeItem('authToken');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userId');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('columnVisibilityClient');
      localStorage.removeItem('columnVisibilityProperty');
      
      // Show error message
      toast.error('Session expired. Please login again.');
      
      // Redirect to login
      window.location.href = '/login';
      return Promise.reject(error);
    }
    
    if (error.response?.status === 403) {
      toast.error('Access forbidden. You do not have permission.');
    }
    
    if (error.response?.status >= 500) {
      toast.error('Server error. Please try again later.');
    }
    
    // Handle timeout errors
    if (error.code === 'ECONNABORTED') {
      toast.error('Request timeout. Please check your connection.');
    }
    
    return Promise.reject(error);
  }
);

// Helper function for handling API errors
export const handleApiError = (error, showToast = true) => {
  const message = error.response?.data?.message || 'An error occurred';
  
  if (showToast && error.response?.status !== 401) {
    toast.error(message);
  }
  
  return message;
};

// Generic API methods
export const apiService = {
  // GET request
  get: async (url, config = {}) => {
    const response = await apiClient.get(url, config);
    return response.data;
  },

  // POST request
  post: async (url, data = {}, config = {}) => {
    const response = await apiClient.post(url, data, config);
    return response.data;
  },

  // PUT request
  put: async (url, data = {}, config = {}) => {
    const response = await apiClient.put(url, data, config);
    return response.data;
  },

  // DELETE request
  delete: async (url, config = {}) => {
    const response = await apiClient.delete(url, config);
    return response.data;
  },

  // GET request that returns the raw axios response (including headers).
  // Useful for binary downloads where the caller needs Content-Disposition,
  // Content-Type, etc. Goes through the same interceptor so auth + 401
  // handling stay consistent with normal calls.
  getBlob: async (url, config = {}) => {
    return apiClient.get(url, { ...config, responseType: 'blob' });
  },

  // PATCH request
  patch: async (url, data = {}, config = {}) => {
    const response = await apiClient.patch(url, data, config);
    return response.data;
  }
};

export default apiClient;